import { useEffect, useMemo, useState } from 'react'

import { pickComparisonCustomer } from '@/lib/invoiceCustomerDiff'
import type { SearchOption } from '@/components/ui/Search'
import type {
  BaselineState,
  CalloutDetailsScreenProps,
  CalloutDetailsSavePayload,
  ContactDetails,
  FormInputs,
  InvoiceDetails,
} from './CalloutDetails.types'
import { formatCalloutStatus, type CalloutStatus } from '@/db/domain/calloutStatus'
import {
  addHourAfter,
  addHourBefore,
  isTimeOnOrAfter,
} from '@/lib/timeWindow'
import { fromUtcIsoToZaParts, fromZaLocalToUtcIso } from '@/lib/datetime'

const formatDateFromIso = (iso: string) => (iso ? fromUtcIsoToZaParts(iso).day : '')
const formatTimeFromIso = (iso: string) => (iso ? fromUtcIsoToZaParts(iso).time : '')

const defaultStart = '09:00'
const defaultEnd = '10:00'

const normalizeValue = (value?: string) => value ?? ''

export function useCalloutDetailsSections(props: CalloutDetailsScreenProps) {
  const {
    serviceId,
    customerId,
    customerPublicId,
    customerName,
    status,
    statusTransitions,
    contactAddress,
    contactName,
    contactPhone,
    contactEmail,
    invoiceName,
    invoiceAddress,
    invoiceEmail,
    invoicePhone,
    invoiceVat,
    invoiceId,
    issueSummary,
    startTime,
    endTime,
    isWholeDay,
    notes,
    newNoteAuthorName = 'You',
    onSaveDetails,
    onChangeStatus,
    onChangeCustomer,
    linkedCustomer,
    onAddNote,
    onUpdateNote,
    onDeleteNote,
  } = props

  const resolvedIssue = issueSummary ?? ''
  const resolvedAddress = contactAddress ?? invoiceAddress ?? ''

  const [day, setDay] = useState(formatDateFromIso(startTime))
  const [startTimePart, setStartTimePart] = useState(
    isWholeDay ? '' : formatTimeFromIso(startTime),
  )
  const [endTimePart, setEndTimePart] = useState(
    isWholeDay ? '' : formatTimeFromIso(endTime),
  )
  const [wholeDay, setWholeDay] = useState(isWholeDay)
  const [issueText, setIssueText] = useState(resolvedIssue)
  const [contactAddressInput, setContactAddress] = useState(resolvedAddress)
  const [contactNameInput, setContactNameInput] = useState(contactName ?? '')
  const [contactPhoneInput, setContactPhoneInput] = useState(contactPhone ?? '')
  const [contactEmailInput, setContactEmailInput] = useState(
    contactEmail ?? '',
  )
  const [invoiceNameInput, setInvoiceNameInput] = useState(
    invoiceName ?? '',
  )
  const [invoiceAddressInput, setInvoiceAddressInput] = useState(
    invoiceAddress ?? '',
  )
  const [invoiceEmailInput, setInvoiceEmailInput] = useState(
    invoiceEmail ?? '',
  )
  const [invoicePhoneInput, setInvoicePhoneInput] = useState(
    invoicePhone ?? '',
  )
  const [invoiceVatInput, setInvoiceVatInput] = useState(invoiceVat ?? '')
  const [invoiceIdInput, setInvoiceIdInput] = useState(invoiceId ?? '')
  const [selectedCustomer, setSelectedCustomer] =
    useState<SearchOption | null>(() =>
      customerId ? { id: customerId, name: customerName } : null,
    )
  const [statusSelection, setStatusSelection] = useState(status)
  const [baseline, setBaseline] = useState<BaselineState>(() => ({
    customerId: customerId ?? null,
    customerName,
    day: formatDateFromIso(startTime),
    startTimePart: isWholeDay ? '' : formatTimeFromIso(startTime),
    endTimePart: isWholeDay ? '' : formatTimeFromIso(endTime),
    wholeDay: isWholeDay,
    contactAddressInput: resolvedAddress,
    contactNameInput: contactName ?? '',
    contactPhoneInput: contactPhone ?? '',
    contactEmailInput: contactEmail ?? '',
    invoiceNameInput: invoiceName ?? '',
    invoiceAddressInput: invoiceAddress ?? '',
    invoiceEmailInput: invoiceEmail ?? '',
    invoicePhoneInput: invoicePhone ?? '',
    invoiceVatInput: invoiceVat ?? '',
    invoiceIdInput: invoiceId ?? '',
    issueText: resolvedIssue,
    status,
  }))

  useEffect(() => {
    setDay(formatDateFromIso(startTime))
    setStartTimePart(isWholeDay ? '' : formatTimeFromIso(startTime))
    setEndTimePart(isWholeDay ? '' : formatTimeFromIso(endTime))
    setWholeDay(isWholeDay)
    setIssueText(resolvedIssue)
    setContactAddress(resolvedAddress)
    setContactNameInput(contactName ?? '')
    setContactPhoneInput(contactPhone ?? '')
    setContactEmailInput(contactEmail ?? '')
    setInvoiceNameInput(invoiceName ?? '')
    setInvoiceAddressInput(invoiceAddress ?? '')
    setInvoiceEmailInput(invoiceEmail ?? '')
    setInvoicePhoneInput(invoicePhone ?? '')
    setInvoiceVatInput(invoiceVat ?? '')
    setInvoiceIdInput(invoiceId ?? '')
    setSelectedCustomer(
      customerId ? { id: customerId, name: customerName } : null,
    )
    setStatusSelection(status)
    setBaseline({
      customerId: customerId ?? null,
      customerName,
      day: formatDateFromIso(startTime),
      startTimePart: isWholeDay ? '' : formatTimeFromIso(startTime),
      endTimePart: isWholeDay ? '' : formatTimeFromIso(endTime),
      wholeDay: isWholeDay,
      contactAddressInput: resolvedAddress,
      contactNameInput: contactName ?? '',
      contactPhoneInput: contactPhone ?? '',
      contactEmailInput: contactEmail ?? '',
      invoiceNameInput: invoiceName ?? '',
      invoiceAddressInput: invoiceAddress ?? '',
      invoiceEmailInput: invoiceEmail ?? '',
      invoicePhoneInput: invoicePhone ?? '',
      invoiceVatInput: invoiceVat ?? '',
      invoiceIdInput: invoiceId ?? '',
      issueText: resolvedIssue,
      status,
    })
  }, [
    contactAddress,
    contactEmail,
    contactName,
    contactPhone,
    customerId,
    customerName,
    endTime,
    invoiceAddress,
    invoiceEmail,
    invoiceId,
    invoiceName,
    invoicePhone,
    invoiceVat,
    isWholeDay,
    issueSummary,
    status,
    startTime,
  ])

  const isLocked = status === 'Invoiced' || status === 'Cancelled'
  // While amending a locked record the fields reopen, but the per-section Save
  // buttons stay disabled: the batch is committed once, as a single addendum.
  const isImmutable = isLocked && !props.amending
  const availableStatuses = statusTransitions.filter(
    (item) => item !== status,
  )

  const displayStartTime = startTimePart || defaultStart
  const displayEndTime = endTimePart || defaultEnd

  const { resolvedStart, resolvedEnd } = useMemo(() => {
    const resolvedDay = day || formatDateFromIso(startTime)
    const safeStartTime =
      wholeDay || !startTimePart ? defaultStart : startTimePart
    const startIso = fromZaLocalToUtcIso(resolvedDay, safeStartTime)

    let endIso: string
    if (wholeDay) {
      endIso = fromZaLocalToUtcIso(resolvedDay, '23:59')
    } else if (endTimePart) {
      endIso = fromZaLocalToUtcIso(resolvedDay, endTimePart)
    } else {
      const [hourStr, minuteStr] = safeStartTime.split(':')
      const hour = Number(hourStr ?? '9')
      const endHour = String(Math.min(23, hour + 1)).padStart(2, '0')
      endIso = fromZaLocalToUtcIso(resolvedDay, `${endHour}:${minuteStr ?? '00'}`)
    }

    return { resolvedStart: startIso, resolvedEnd: endIso }
  }, [day, endTimePart, startTime, startTimePart, wholeDay])

  const handleStartTimeChange = (value: string) => {
    setStartTimePart(value)
    setEndTimePart((currentEnd) =>
      !currentEnd ||
      currentEnd.trim().length === 0 ||
      isTimeOnOrAfter(value, currentEnd)
        ? addHourAfter(value)
        : currentEnd,
    )
  }

  const handleEndTimeChange = (value: string) => {
    setEndTimePart(value)
    setStartTimePart((currentStart) =>
      !currentStart || isTimeOnOrAfter(currentStart, value)
        ? addHourBefore(value)
        : currentStart,
    )
  }

  const customerDirty =
    (selectedCustomer?.id ?? null) !== baseline.customerId
  const bookingDirty =
    day !== baseline.day ||
    startTimePart !== baseline.startTimePart ||
    endTimePart !== baseline.endTimePart ||
    wholeDay !== baseline.wholeDay
  const issueDirty = issueText !== baseline.issueText
  const contactDirty =
    contactAddressInput !== baseline.contactAddressInput ||
    contactNameInput !== baseline.contactNameInput ||
    contactPhoneInput !== baseline.contactPhoneInput ||
    contactEmailInput !== baseline.contactEmailInput
  const invoiceDirty =
    invoiceNameInput !== baseline.invoiceNameInput ||
    invoiceAddressInput !== baseline.invoiceAddressInput ||
    invoiceEmailInput !== baseline.invoiceEmailInput ||
    invoicePhoneInput !== baseline.invoicePhoneInput ||
    invoiceVatInput !== baseline.invoiceVatInput ||
    invoiceIdInput !== baseline.invoiceIdInput
  const customerSaveEnabled =
    !isLocked &&
    customerDirty &&
    Boolean(onChangeCustomer) &&
    Boolean(selectedCustomer?.id)
  const bookingSaveEnabled =
    !isLocked && bookingDirty && Boolean(onSaveDetails)
  const issueSaveEnabled =
    !isLocked && issueDirty && Boolean(onSaveDetails)
  const contactSaveEnabled =
    !isLocked && contactDirty && Boolean(onSaveDetails)
  const invoiceSaveEnabled =
    !isLocked && invoiceDirty && Boolean(onSaveDetails)
  const updateBaselineAfterSave = (inputs: Partial<FormInputs>) => {
    setBaseline((prev) => ({
      ...prev,
      day,
      startTimePart,
      endTimePart,
      wholeDay,
      ...inputs,
    }))
  }

  const handleSaveDetails = () => {
    if (!onSaveDetails || isLocked) return

    const payload: CalloutDetailsSavePayload = {
      contactAddress: contactAddressInput,
      contactName: normalizeValue(contactNameInput) || undefined,
      contactPhone: normalizeValue(contactPhoneInput) || undefined,
      contactEmail: normalizeValue(contactEmailInput) || undefined,
      invoiceName: normalizeValue(invoiceNameInput) || undefined,
      invoiceAddress: normalizeValue(invoiceAddressInput) || undefined,
      invoiceEmail: normalizeValue(invoiceEmailInput) || undefined,
      invoicePhone: normalizeValue(invoicePhoneInput) || undefined,
      invoiceVat: normalizeValue(invoiceVatInput) || undefined,
      invoiceId: normalizeValue(invoiceIdInput) || undefined,
      issueSummary: issueText,
      startTime: resolvedStart,
      endTime: resolvedEnd,
      isWholeDay: wholeDay,
    }

    onSaveDetails(payload)

    updateBaselineAfterSave({
      contactAddressInput,
      contactNameInput,
      contactPhoneInput,
      contactEmailInput,
      invoiceNameInput,
      invoiceAddressInput,
      invoiceEmailInput,
      invoicePhoneInput,
      invoiceVatInput,
      invoiceIdInput,
      issueText,
    })
  }

  const handleSaveCustomer = () => {
    if (
      !onChangeCustomer ||
      isImmutable ||
      !selectedCustomer?.id ||
      !customerDirty
    ) {
      return
    }
    onChangeCustomer(String(selectedCustomer.id))
    setBaseline((prev) => ({
      ...prev,
      customerId: selectedCustomer.id as string,
      customerName: selectedCustomer.name,
    }))
  }

  const handleApplyStatus = async (nextStatus: CalloutStatus, reason?: string) => {
    if (!onChangeStatus || !availableStatuses.includes(nextStatus)) return
    await Promise.resolve(onChangeStatus(nextStatus, reason))
    setStatusSelection(nextStatus)
    setBaseline((prev) => ({
      ...prev,
      status: nextStatus,
    }))
  }

  const handleResetCustomer = () => {
    if (!customerSaveEnabled) return
    setSelectedCustomer(
      baseline.customerId
        ? { id: baseline.customerId, name: baseline.customerName }
        : null,
    )
  }

  const handleResetBooking = () => {
    if (!bookingSaveEnabled) return
    setDay(baseline.day)
    setStartTimePart(baseline.startTimePart)
    setEndTimePart(baseline.endTimePart)
    setWholeDay(baseline.wholeDay)
  }

  const handleResetIssue = () => {
    if (!issueSaveEnabled) return
    setIssueText(baseline.issueText)
  }

  const handleResetContact = () => {
    if (!contactSaveEnabled) return
    setContactAddress(baseline.contactAddressInput)
    setContactNameInput(baseline.contactNameInput)
    setContactPhoneInput(baseline.contactPhoneInput)
    setContactEmailInput(baseline.contactEmailInput)
  }

  const handleResetInvoice = () => {
    if (!invoiceSaveEnabled) return
    setInvoiceNameInput(baseline.invoiceNameInput)
    setInvoiceAddressInput(baseline.invoiceAddressInput)
    setInvoiceEmailInput(baseline.invoiceEmailInput)
    setInvoicePhoneInput(baseline.invoicePhoneInput)
    setInvoiceVatInput(baseline.invoiceVatInput)
    setInvoiceIdInput(baseline.invoiceIdInput)
  }

  const handleSelectCustomer = (option: SearchOption | null) => {
    setSelectedCustomer(option)
  }

  const contactFormValues = {
    contactAddress: contactAddressInput,
    contactName: contactNameInput,
    contactPhone: contactPhoneInput,
    contactEmail: contactEmailInput,
  }

  const invoiceFormValues = {
    invoiceName: invoiceNameInput,
    invoiceAddress: invoiceAddressInput,
    invoiceEmail: invoiceEmailInput,
    invoicePhone: invoicePhoneInput,
    invoiceVat: invoiceVatInput,
    invoiceId: invoiceIdInput,
  }

  /**
   * The amendable fields as the form currently holds them. The addendum card
   * diffs this against the values in force to build the change list.
   */
  const amendDraft: Record<string, string> = {
    customerId: selectedCustomer?.id != null ? String(selectedCustomer.id) : '',
    customerName: selectedCustomer?.name ?? '',
    issueSummary: issueText,
    contactAddress: contactAddressInput,
    contactName: contactNameInput,
    contactPhone: contactPhoneInput,
    contactEmail: contactEmailInput,
    ...invoiceFormValues,
  }

  return {
    serviceId,
    statusLabel: formatCalloutStatus(status),
    isImmutable,
    isLocked,
    amendDraft,
    customerSection: {
      customerName,
      customerId,
      customerPublicId,
      selectedCustomer,
      isImmutable,
      saveEnabled: customerSaveEnabled,
      details: linkedCustomer
        ? {
            fullName: linkedCustomer.fullName,
            externalId: linkedCustomer.externalId,
            contactEmail: linkedCustomer.contactEmail,
            contactPhone: linkedCustomer.contactPhone,
            contactAddress: linkedCustomer.contactAddress,
            invoiceName: linkedCustomer.invoiceName,
            invoiceEmail: linkedCustomer.invoiceEmail,
            invoicePhone: linkedCustomer.invoicePhone,
            invoiceAddress: linkedCustomer.invoiceAddress,
            invoiceVat: linkedCustomer.invoiceVat,
          }
        : undefined,
      onSelectCustomer: handleSelectCustomer,
      onSaveCustomer: handleSaveCustomer,
      onResetCustomer: handleResetCustomer,
    },
    bookingSection: {
      day,
      wholeDay,
      displayStartTime,
      displayEndTime,
      isImmutable,
      saveEnabled: bookingSaveEnabled,
      onDayChange: (value: string) => setDay(value),
      onWholeDayChange: (checked: boolean) => setWholeDay(checked),
      onStartChange: handleStartTimeChange,
      onEndChange: handleEndTimeChange,
      onSave: handleSaveDetails,
      onReset: handleResetBooking,
    },
    issueSection: {
      issueText,
      isImmutable,
      saveEnabled: issueSaveEnabled,
      onChange: (value: string) => setIssueText(value),
      onSave: handleSaveDetails,
      onReset: handleResetIssue,
    },
    statusSection: {
      statusSelection,
      availableStatuses,
      formatStatus: formatCalloutStatus,
      onApplyStatus: handleApplyStatus,
    },
    contactSection: {
      ...contactFormValues,
      isImmutable,
      saveEnabled: contactSaveEnabled,
      onChange: (fields: Partial<ContactDetails>) => {
        if (fields.contactAddress !== undefined)
          setContactAddress(fields.contactAddress)
        if (fields.contactName !== undefined)
          setContactNameInput(fields.contactName)
        if (fields.contactPhone !== undefined)
          setContactPhoneInput(fields.contactPhone)
        if (fields.contactEmail !== undefined)
          setContactEmailInput(fields.contactEmail)
      },
      onSave: handleSaveDetails,
      onReset: handleResetContact,
    },
    invoiceSection: {
      ...invoiceFormValues,
      customerInvoice: pickComparisonCustomer({
        selected: selectedCustomer as never,
        currentCustomerId: customerId,
        linked: linkedCustomer ?? undefined,
      }),
      isImmutable,
      saveEnabled: invoiceSaveEnabled,
      onChange: (fields: Partial<InvoiceDetails>) => {
        if (fields.invoiceName !== undefined)
          setInvoiceNameInput(fields.invoiceName)
        if (fields.invoiceEmail !== undefined)
          setInvoiceEmailInput(fields.invoiceEmail)
        if (fields.invoicePhone !== undefined)
          setInvoicePhoneInput(fields.invoicePhone)
        if (fields.invoiceAddress !== undefined)
          setInvoiceAddressInput(fields.invoiceAddress)
        if (fields.invoiceVat !== undefined)
          setInvoiceVatInput(fields.invoiceVat)
        if (fields.invoiceId !== undefined)
          setInvoiceIdInput(fields.invoiceId)
      },
      onSave: handleSaveDetails,
      onReset: handleResetInvoice,
    },
    notesSection: {
      notes,
      newNoteAuthorName,
      onAddNote,
      onUpdateNote,
      onDeleteNote,
      isImmutable,
    },
  }
}
